import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Pediatrics = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Actual pediatric products based on the images in public/product_img/PEDIATRIC
  const products = [
    {
      id: 'aerobot',
      name: 'AEROBOT',
      description: 'Respiratory support medication for children with asthma and breathing difficulties.',
      dosageForm: 'Inhaler',
      category: 'Respiratory',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/AEROBOT.png'
    },
    {
      id: 'bolarace-bc',
      name: 'BOLARACE-BC',
      description: 'Combination formula for effective treatment of pediatric respiratory infections.',
      dosageForm: 'Syrup',
      category: 'Respiratory',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/BOLARACE-BC.png'
    },
    {
      id: 'cortimust',
      name: 'CORTIMUST',
      description: 'Anti-inflammatory corticosteroid for managing acute allergic conditions in children.',
      dosageForm: 'Syrup',
      category: 'Anti-inflammatory',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/CORTIMUST.png'
    },
    {
      id: 'dolosmile-mf',
      name: 'DOLOSMILE-MF',
      description: 'Dual-action pain reliever and fever reducer for children, gentle on the stomach.',
      dosageForm: 'Suspension',
      category: 'Analgesic',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/DOLOSMILE-MF.png'
    },
    {
      id: 'duosmart-ld',
      name: 'DUOSMART-LD',
      description: 'Advanced formulation for managing cold and cough symptoms in children.',
      dosageForm: 'Syrup',
      category: 'Cold & Cough',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/DUOSMART-LD.png'
    },
    {
      id: 'duosmart',
      name: 'DUOSMART',
      description: 'Comprehensive cold and flu relief for children with multiple symptoms.',
      dosageForm: 'Syrup',
      category: 'Cold & Cough',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/DUOSMART.png'
    },
    {
      id: 'flora-123',
      name: 'FLORA-123',
      description: 'Triple-strain probiotic formulation for optimal digestive health in children.',
      dosageForm: 'Sachets',
      category: 'Probiotic',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/FLORA-123.png'
    },
    {
      id: 'flushield',
      name: 'FLUSHIELD',
      description: 'Immune-boosting formula to help children fight seasonal illnesses and infections.',
      dosageForm: 'Syrup',
      category: 'Immunity',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/FLUSHIELD.png'
    },
    {
      id: 'granismart',
      name: 'GRANISMART',
      description: 'Anti-emetic medication to treat nausea and vomiting in children.',
      dosageForm: 'Suspension',
      category: 'Anti-emetic',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/GRANISMART.png'
    },
    {
      id: 'ibupario',
      name: 'IBUPARIO',
      description: 'Child-friendly ibuprofen formulation for pain and fever relief with minimal side effects.',
      dosageForm: 'Suspension',
      category: 'Analgesic',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/IBUPARIO.png'
    },
    {
      id: 'joy-d3',
      name: 'JOY-D3',
      description: 'Vitamin D3 supplement specifically formulated for growing children to support bone development.',
      dosageForm: 'Drops',
      category: 'Nutritional',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/JOY-D3.png'
    },
    {
      id: 'pario-cold',
      name: 'PARIO-COLD',
      description: 'Multi-symptom cold relief formulation for children, safe and effective.',
      dosageForm: 'Syrup',
      category: 'Cold & Cough',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/PARIO-COLD.png'
    },
    {
      id: 'timirio-dcr',
      name: 'TIMIRIO-DCR',
      description: 'Decongestant and cough relief syrup formulated for children with persistent symptoms.',
      dosageForm: 'Syrup',
      category: 'Respiratory',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/TIMIRIO-DCR.png'
    },
    {
      id: 'timirio-ls',
      name: 'TIMIRIO-LS',
      description: 'Gentle formula for managing mild to moderate respiratory symptoms in children.',
      dosageForm: 'Syrup',
      category: 'Respiratory',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/TIMIRIO-LS.png'
    },
    {
      id: 'winsmile',
      name: 'WINSMILE',
      description: 'Child-friendly multivitamin formulation to support overall growth and development.',
      dosageForm: 'Syrup',
      category: 'Nutritional',
      image: process.env.PUBLIC_URL + '/product_img/PEDIATRIC/WINSMILE.png'
    }
  ];

  const [selectedProduct, setSelectedProduct] = React.useState(null);

  const openProductDetails = (product) => {
    setSelectedProduct(product);
  };

  const closeProductDetails = () => {
    setSelectedProduct(null);
  };

  // Group products by category for better organization
  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {});

  const categories = Object.keys(groupedProducts);

  return (
    <div className="pt-20">
      {/* Header */}
      <div className="bg-pediatric text-white py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center mb-4">
              <Link to="/products" className="flex items-center text-white hover:text-pediatric-light transition-colors">
                <FaArrowLeft className="mr-2" /> Back to Categories
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Pediatric Products</h1>
            <p className="text-xl text-gray-100 max-w-3xl mb-8">
              Our specialized range of pediatric medicines and supplements designed 
              specifically for infants, children, and adolescents to support their 
              growth and development.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-gray-50 py-6 sticky top-0 z-10 shadow-sm">
        <div className="container-custom">
          <div className="overflow-x-auto">
            <div className="flex space-x-2 md:space-x-4 min-w-max">
              <button 
                className="px-4 py-2 rounded-full bg-pediatric text-white text-sm font-medium"
                onClick={() => document.getElementById('all-products').scrollIntoView({ behavior: 'smooth' })}
              >
                All Products
              </button>
              {categories.map(category => (
                <button 
                  key={category}
                  className="px-4 py-2 rounded-full bg-white border border-pediatric text-pediatric text-sm font-medium hover:bg-pediatric/10 transition-colors"
                  onClick={() => document.getElementById(category).scrollIntoView({ behavior: 'smooth' })}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* All Products */}
      <div id="all-products" className="container-custom py-12">
        <h2 className="text-2xl font-bold mb-8 text-gray-800">All Pediatric Products</h2>
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              variants={fadeIn}
              className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className="h-64 bg-pediatric/5 flex items-center justify-center p-4">
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-pediatric">{product.name}</h3>
                  <span className="bg-pediatric/10 text-pediatric text-xs px-2 py-1 rounded-full">
                    {product.dosageForm}
                  </span>
                </div>
                <p className="text-gray-600 mb-4">
                  {product.description}
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">
                    {product.category}
                  </span>
                  <button 
                    onClick={() => openProductDetails(product)}
                    className="text-pediatric font-medium hover:underline flex items-center"
                  >
                    View Details <FaInfoCircle className="ml-2" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Products By Category */}
      {categories.map(category => (
        <div 
          id={category}
          key={category}
          className="py-16 bg-gray-50 even:bg-white"
        >
          <div className="container-custom">
            <h2 className="text-2xl font-bold mb-8 text-gray-800">{category} Products</h2>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {groupedProducts[category].map((product) => (
                <motion.div
                  key={product.id}
                  variants={fadeIn}
                  className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="h-64 bg-pediatric/5 flex items-center justify-center p-4">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-pediatric">{product.name}</h3>
                      <span className="bg-pediatric/10 text-pediatric text-xs px-2 py-1 rounded-full">
                        {product.dosageForm}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">
                      {product.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-500">
                        {product.category}
                      </span>
                      <button 
                        onClick={() => openProductDetails(product)}
                        className="text-pediatric font-medium hover:underline flex items-center"
                      >
                        View Details <FaInfoCircle className="ml-2" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      ))}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto"
          >
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-pediatric">{selectedProduct.name}</h2>
                <button 
                  onClick={closeProductDetails}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-center">
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.name} 
                    className="max-w-full max-h-80 object-contain"
                  />
                </div>
                
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">Description</h3>
                    <p className="text-gray-600">{selectedProduct.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Category</h3>
                      <p className="text-gray-600">{selectedProduct.category}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Dosage Form</h3>
                      <p className="text-gray-600">{selectedProduct.dosageForm}</p>
                    </div>
                  </div>
                  
                  <div className="bg-pediatric/5 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Key Benefits</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      {selectedProduct.category === 'Respiratory' && (
                        <>
                          <li>Helps clear airways and improve breathing</li>
                          <li>Relieves coughing and congestion</li>
                          <li>Specially formulated for children's respiratory systems</li>
                        </>
                      )}
                      {selectedProduct.category === 'Analgesic' && (
                        <>
                          <li>Fast-acting pain relief for children</li>
                          <li>Reduces fever effectively</li>
                          <li>Gentle on sensitive stomachs</li>
                        </>
                      )}
                      {selectedProduct.category === 'Cold & Cough' && (
                        <>
                          <li>Alleviates multiple cold and flu symptoms</li>
                          <li>Child-friendly taste and formulation</li>
                          <li>Non-drowsy formula for daytime use</li>
                        </>
                      )}
                      {selectedProduct.category === 'Nutritional' && (
                        <>
                          <li>Supports overall growth and development</li>
                          <li>Helps fill nutritional gaps in children's diets</li>
                          <li>Great tasting formula that kids enjoy</li>
                        </>
                      )}
                      {selectedProduct.category === 'Probiotic' && (
                        <>
                          <li>Promotes healthy gut flora in children</li>
                          <li>Helps improve digestive issues</li>
                          <li>Supports immune system function</li>
                        </>
                      )}
                      {selectedProduct.category === 'Immunity' && (
                        <>
                          <li>Strengthens children's natural defenses</li>
                          <li>Helps fight common seasonal illnesses</li>
                          <li>Contains essential vitamins and minerals</li>
                        </>
                      )}
                      {selectedProduct.category === 'Anti-inflammatory' && (
                        <>
                          <li>Reduces inflammation and related symptoms</li>
                          <li>Provides relief from allergic reactions</li>
                          <li>Pediatric dosing for safety</li>
                        </>
                      )}
                      {selectedProduct.category === 'Anti-emetic' && (
                        <>
                          <li>Effective control of nausea and vomiting</li>
                          <li>Gentle action suitable for children</li>
                          <li>Fast-acting relief when needed most</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  * Consult with a healthcare professional before starting any new medication or supplement. Dosage should be determined by a healthcare provider based on the child's age and weight.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Pediatrics;
